import { Link } from "@tanstack/react-router";
import { Col, Input, Row } from "antd";
import { useLoginMutation } from "api/Auth/hooks.ts";
import queryClient from "api/queryClient.ts";
import { AxiosError } from "axios";
import { AuthAlert } from "components/UI/AuthAlert/AuthAlert.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AuthFormWrapper } from "components/Wrappers/AuthFormWrapper/AuthFormWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { FC, useState } from "react";
import { FormItem } from "react-hook-form-antd";
import { ApiErrorResponse } from "types/models";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";

import { LoginFormData, schema } from "./formSchema";

interface LoginFormContentProps {
    submitError?: string | null;
    isSubmitting?: boolean;
}

export const LoginFormContent: FC<LoginFormContentProps> = ({ submitError, isSubmitting }) => {
    const { t: tC } = usePaldockTranslation("common");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tAuth } = usePaldockTranslation("auth.login");
    const generalFormUtils = useGeneralForm<LoginFormData>();
    const { control, fieldNames } = generalFormUtils;

    return (
        <AuthFormWrapper
            buttonText={tC("login")}
            loading={isSubmitting}
            buttonDisabled={isSubmitting}
            beforeButton={
                <div className="flex justify-end mt-1 mb-1">
                    <Link
                        to="/auth/forgot-password"
                        className="text-md text-black underline hover:no-underline hover:text-black"
                    >
                        {tAuth("links.forgot_password")}
                    </Link>
                </div>
            }
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.email.name}
                        label={tFields("email.label")}
                        required
                        className="!mb-0"
                    >
                        <Input type="email" placeholder={tFields("email.placeholder")} />
                    </FormItem>
                </Col>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.password.name}
                        label={tFields("password.label")}
                        required
                        className="!mb-0"
                    >
                        <Input.Password placeholder={tFields("password.placeholder")} />
                    </FormItem>
                </Col>
                {submitError && (
                    <Col span={24}>
                        <AuthAlert type="error" message={submitError} />
                    </Col>
                )}
            </Row>
        </AuthFormWrapper>
    );
};

export const LoginForm: FC = () => {
    const { t: tAuth } = usePaldockTranslation("auth.login");
    const userUtils = useUser();
    const [submitError, setSubmitError] = useState<string | null>(null);
    // The login mutation settles before the post-login redirect (which awaits its own "/me" fetch)
    // finishes — without this, the submit button flashes back to enabled for that gap.
    const [isRedirecting, setIsRedirecting] = useState(false);

    const getSubmitErrorMessage = (error: AxiosError<ApiErrorResponse>) => {
        const validationMessage = extractValidationErrorMessages(error)[0];

        if (validationMessage) {
            return validationMessage;
        }

        if (error.response?.status === 401 || error.response?.status === 422) {
            return tAuth("messages.invalid_credentials");
        }

        return tAuth("messages.login_failed");
    };

    const loginMutation = useLoginMutation(
        {
            onSuccess: (response) => {
                setIsRedirecting(true);
                queryClient.removeQueries({ queryKey: ["me"] });
                userUtils.loginUser(response?.token || "", response.user);
            },
            onError: (error) => setSubmitError(getSubmitErrorMessage(error)),
        },
        true
    );

    const defaultValues = {
        email: "",
        password: "",
    };

    const onSubmit = (data: LoginFormData) => {
        setSubmitError(null);
        loginMutation.mutate(data);
    };

    return (
        <GeneralFormProvider<LoginFormData>
            schema={schema}
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            requiredMark={false}
        >
            <LoginFormContent submitError={submitError} isSubmitting={loginMutation.isPending || isRedirecting} />
        </GeneralFormProvider>
    );
};
